import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface TimesheetItem {
  id?: string;
  line_number: number;
  employee_id: string;
  working_days: string;
  vacation_days: string;
  sick_days: string;
  absence_days: string;
  worked_hours?: string | null;  // фіча #3: години для погодинної оплати (RATE)
  // Посуточна сітка (режим «По днях»): [{day,code,hours}] | null (alembic 0300).
  days?: { day: number; code: string; hours: number }[] | null;
  comment?: string | null;
}

export interface TimesheetListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  norm_days: number;
  posted: boolean;
  deletion_mark: boolean;
  // Block 5.3f:
  state?: CancellableState;
  external_source?: string | null;
}

export interface Timesheet extends TimesheetListItem {
  posted_at: string | null;
  comment: string | null;
  items: TimesheetItem[];
  // Block 5.3f:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface TimesheetCreate {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  norm_days: number;
  comment?: string | null;
  items: TimesheetItem[];
}

export type TimesheetUpdate = Partial<TimesheetCreate>;

// Авто-заповнення за графіком (Порція C/D).
export interface AutofillDay {
  day: number;
  is_work: boolean;
  hours: number;
}
export interface AutofillItem {
  employee_id: string;
  schedule_id: string | null;
  schedule_name: string | null;
  working_days: number;
  worked_hours: number;
  hours_per_day: number | null;  // годин/день за графіком (для перерахунку годин)
  /** ч.1209: причина неробочих днів — «відпустка для догляду за дитиною з … по …». */
  absence_note?: string | null;
  days: AutofillDay[];
}
export interface AutofillResponse {
  period_year: number;
  period_month: number;
  days_in_month: number;
  norm_days: number;
  norm_hours: number;
  items: AutofillItem[];
}

export interface TimesheetRegistryRow {
  id: string;
  kind: 'monthly' | 'daily';
  organization_id: string;
  document_number: string;
  period_date: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
}

export const timesheets = {
  // Єдиний реєстр місячних + денних табелів (серверна пагінація, 2026-08-23).
  registry: (params?: {
    organization_id?: string; kind?: 'monthly' | 'daily';
    period_year?: number; period_month?: number; period_from?: string; period_to?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) =>
    api.get<Page<TimesheetRegistryRow>>('/timesheets/registry', { params }).then((r) => r.data),
  list: (params?: {
    organization_id?: string;
    period_year?: number;
    period_month?: number; period_from?: string; period_to?: string;
    posted?: boolean;
    limit?: number;
  }) =>
    api.get<Page<TimesheetListItem>>('/timesheets', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<Timesheet>(`/timesheets/${id}`).then((r) => r.data),
  create: (payload: TimesheetCreate) =>
    api.post<Timesheet>('/timesheets', payload).then((r) => r.data),
  update: (id: string, payload: TimesheetUpdate) =>
    api.patch<Timesheet>(`/timesheets/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/timesheets/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<Timesheet>(`/timesheets/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<Timesheet>(`/timesheets/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/timesheets/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  autofill: (payload: {
    organization_id: string;
    period_year: number;
    period_month: number;
    employee_ids: string[];
    is_wartime?: boolean;
  }) =>
    api.post<AutofillResponse>('/timesheets/autofill', payload).then((r) => r.data),
  buildFromDaily: (payload: {
    organization_id: string;
    period_year: number;
    period_month: number;
  }) =>
    api.post<BuildFromDailyResponse>('/timesheets/build-from-daily', payload).then((r) => r.data),
  // Годин/день за графіком працівників (для перерахунку годин у «Зведено»).
  hoursPerDay: (payload: { organization_id: string; employee_ids: string[] }) =>
    api.post<Record<string, number | null>>('/timesheets/hours-per-day', payload).then((r) => r.data),
};

// ============ Денний табель (допоміжний для збірки місячного) ============

export type DayCode = 'W' | 'V' | 'S' | 'A' | 'OFF';

export interface DailyTimesheetItem {
  id?: string;
  line_number: number;
  employee_id: string;
  day_code: DayCode;
  worked_hours: number | string;
  comment?: string | null;
}

export interface DailyTimesheetListItem {
  id: string;
  organization_id: string;
  document_number: string;
  day_date: string;
  posted: boolean;
  deletion_mark: boolean;
}

export interface DailyTimesheet extends DailyTimesheetListItem {
  comment: string | null;
  items: DailyTimesheetItem[];
}

export interface DailyTimesheetCreate {
  organization_id: string;
  document_number?: string | null;
  day_date: string;
  comment?: string | null;
  items: DailyTimesheetItem[];
}

export interface BuildFromDailyResponse {
  period_year: number;
  period_month: number;
  daily_count: number;
  items: {
    employee_id: string;
    working_days: number;
    vacation_days: number;
    sick_days: number;
    absence_days: number;
    worked_hours: number;
    days: { day: number; is_work: boolean; hours: number; code: string }[];
  }[];
}

export const dailyTimesheets = {
  list: (params?: { organization_id?: string; day_date?: string; limit?: number }) =>
    api.get<Page<DailyTimesheetListItem>>('/daily-timesheets', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<DailyTimesheet>(`/daily-timesheets/${id}`).then((r) => r.data),
  create: (payload: DailyTimesheetCreate) =>
    api.post<DailyTimesheet>('/daily-timesheets', payload).then((r) => r.data),
  update: (id: string, payload: Partial<DailyTimesheetCreate>) =>
    api.patch<DailyTimesheet>(`/daily-timesheets/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/daily-timesheets/${id}`).then(() => undefined),
};
